import { AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import Button from "@/components/Button";
import { PageHeading, SectionHeading, SrOnly } from "@/components/Layout/Parts";
import Text from "@/components/Text";
import { ResponsiveTable, Table, TableContent, Th } from "@/components/Table";
import { api } from "@/utils/api";
import { MetaBusinessAccountResource } from "@/types/resources";
import TableRow from "./components/TableRow";
import ContentSpinner from "@/components/ContentSpinner";
import Form from "../MetaBusinessAccounts/components/Form";
import InfoCard from "./components/InfoCard";
import ChannelsAttachmentForm from "../MetaBusinessAccounts/components/ChannelsAttachmentForm";

const HeadingsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
`;

const HeaderButtons = styled.div`
  margin-left: auto;
  display: flex;
  gap: 16px;
`;

const NewProjectButton = styled(Button)`
  padding: 0 32px;
  background: transparent;
`;

const ChannelsTable = styled(Table)`
  td {
    padding-top: 16px;
    padding-bottom: 16px;
  }
  td,
  th {
    &:first-child {
      padding-left: 24px;
    }
  }
`;

const MetaBusinessAccount = () => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [showPagesForm, setShowPagesForm] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: account, isLoading } = useQuery(
    ["meta_business_accounts", { id }],
    () =>
      api.get<never, MetaBusinessAccountResource>(
        `/v1/meta-business-accounts/${id}`
      ),
    {
      staleTime: 120 * 1000,
    }
  );

  useEffect(() => {
    if (!id) {
      return navigate("/404", { replace: true });
    }

    if (!isLoading && !account) {
      return navigate("/404", { replace: true });
    }
  }, [id, isLoading, account, navigate]);

  if (isLoading || !account) {
    return <ContentSpinner />;
  }

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <HeadingsRow>
        <PageHeading>{account!.name}</PageHeading>
        <HeaderButtons>
          <NewProjectButton onClick={() => setShowPagesForm(true)}>
            Add Page
          </NewProjectButton>
          <NewProjectButton
            onClick={() => setShowEditForm(true)}
            color="secondary"
          >
            Edit Account
          </NewProjectButton>
        </HeaderButtons>
      </HeadingsRow>
      <SectionHeading>Account Details</SectionHeading>
      <InfoCard account={account!} />
      <SectionHeading>Pages List</SectionHeading>
      <ResponsiveTable>
        <ChannelsTable $fixed>
          <thead>
            <tr>
              <Th>Page Name</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={false}
              isError={false}
              hasData={!!account!.channels!.length}
              colSpan={2}
            >
              {account!.channels!.map((c) => (
                <TableRow key={c.id} channel={c} />
              ))}
            </TableContent>
          </tbody>
        </ChannelsTable>
      </ResponsiveTable>
      <AnimatePresence>
        {showEditForm && (
          <Form
            onClose={() => setShowEditForm(false)}
            editableAccount={account}
          />
        )}
        {showPagesForm && (
          <ChannelsAttachmentForm
            onClose={() => setShowPagesForm(false)}
            accountId={account!.id}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default MetaBusinessAccount;
